import {
  Box,
  Button,
  Flex,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { CellContext, ColumnMeta, Row, Table } from '@tanstack/react-table';
import { isNil, round } from 'lodash';
import * as React from 'react';
import { useMemo } from 'react';
import invariant from 'tiny-invariant';
import { useFlag } from '@unleash/proxy-client-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'urql';
import { displayNameFromProperty } from '../../../domain/friendly-properties';
import {
  Point,
  SPARKEL_GEOMETRIC_PROPERTY_SET,
  SPARKEL_LOCATION_PROPERTY_SET,
} from '../../../domain/geometry/geometric-types';
import {
  QuantityWithUnits,
  ResolvedQuantity,
} from '../../../domain/property-types';
import {
  ColumnType,
  GetCompanyDatabaseColumnDocument,
  OrderColumnDeepFragment,
  PropertySetType,
  QuantityEnum,
  QuantityOperation,
  QuantityType,
  Unit,
  UnitSettingsType,
} from '../../../gql/graphql';
import {
  Substitution,
  getValidSubstitutions,
  parseAndStringifyDerivedFormula,
} from '../../../utils/formula-utils';
import { formatQuantity } from '../../../utils/quantity-utils';
import SparkelFormulaInputGroup, {
  FormulaColumnKey,
} from '../../common/SparkelFormula';
import SparkelIcon from '../../common/icon/SparkelIcon';
import {
  EditableTableMeta,
  isEditableTableMeta,
  isEditingRow,
} from '../../table/Table';
import { useProjectHistory } from '../project-history/hooks/useProjectHistory';
import DiffNumber from '../project-history/DiffNumber';
import { DiffText } from '../project-history/DiffText';
import { OrderEntryTableRow } from '../aggregated-order-panel/aggregated-order-table/columns';
import MissingPropertyButton from './QuantityMissingPropertyButton';
import { TableEntry, getEntryDiffPath } from './OrderEntriesPanel';
import { UnitTypeToUnit, formatUnit } from 'src/utils/unit-utils';

type DynamicQuantitiesCellProps = CellContext<
  TableEntry,
  ResolvedQuantity | null
>;

type ActiveQuantityCell = {
  columnName: string;
  rowId: string;
} | null;

export type DynamicQuantitiesColumnMeta = {
  handleActiveQuantityCellClick: (
    columnName: string,
    selectedEntry: Row<TableEntry>
  ) => void;
  propertySets: PropertySetType[];
  orderColumns: OrderColumnDeepFragment[];
  projectId: string;
  isDrawingQuantityColumn: boolean;
  activeQuantityCell: ActiveQuantityCell;
  columnName: string;
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
    manuallyApplyFilters?: boolean;
  };
};

function isDynamicQuantitiesColumnMeta(
  meta: ColumnMeta<TableEntry, number | null> | undefined
): meta is DynamicQuantitiesColumnMeta {
  if (!meta) {
    return false;
  }
  let casted = meta as DynamicQuantitiesColumnMeta;
  return (
    !isNil(casted.handleActiveQuantityCellClick) &&
    !isNil(casted.isDrawingQuantityColumn) &&
    !isNil(casted.propertySets) &&
    !isNil(casted.orderColumns) &&
    !isNil(casted.projectId)
  );
}

export default function DynamicQuantitiesCell({
  getValue,
  row,
  column,
  table,
}: DynamicQuantitiesCellProps): React.ReactElement | null {
  const {
    options: { meta: tableMeta },
  } = table;

  invariant(isEditableTableMeta(tableMeta));
  const columnMeta = column.columnDef.meta;
  invariant(isDynamicQuantitiesColumnMeta(columnMeta));
  const {
    orderColumns,
    propertySets,
    projectId,
    columnName,
    handleActiveQuantityCellClick,
    activeQuantityCell,
    isDrawingQuantityColumn,
    projectSettings,
  } = columnMeta;
  const availableSubstitutions: Substitution[] = useMemo(() => {
    const quantityColumnId =
      orderColumns.find((col) => col.columnType === ColumnType.Quantity)?.id ??
      ''; // Default to empty string because if we can't find the quantity column, then we don't need to filter it out
    return getValidSubstitutions(quantityColumnId, orderColumns);
  }, [orderColumns]);

  const enableUnitSettings = useFlag('enable-unit-settings');

  const value = getValue();

  return (
    <QuantityCell
      tableMeta={tableMeta}
      projectId={projectId}
      orderColumns={orderColumns}
      row={row}
      table={table}
      columnId={column.id}
      columnName={columnName}
      columnKey="quantity"
      quantity={row.original.quantity ?? null}
      resolvedValue={value}
      isSomePropertyMissing={!!row.original.isMissingResolvedQuantity}
      propertySets={propertySets}
      availableSubstitutions={availableSubstitutions}
      activeQuantityCell={activeQuantityCell}
      handleActiveQuantityCellClick={handleActiveQuantityCellClick}
      isDrawingQuantityColumn={isDrawingQuantityColumn}
      allowSumOnly={true}
      projectSettings={{
        ...projectSettings,
        unitSettings: enableUnitSettings
          ? projectSettings.unitSettings
          : undefined,
      }}
      showUnitSymbol={false}
    />
  );
}

// eslint-disable-next-line complexity
export function QuantityCell({
  tableMeta,
  projectId,
  orderColumns,
  row,
  table,
  columnId,
  columnName,
  columnKey,
  quantity,
  resolvedValue,
  isSomePropertyMissing,
  propertySets,
  availableSubstitutions,
  activeQuantityCell,
  handleActiveQuantityCellClick,
  isDrawingQuantityColumn,
  allowSumOnly = false,
  projectSettings,
  showUnitSymbol = true,
}: {
  tableMeta: EditableTableMeta;
  projectId: string;
  row: Row<TableEntry>;
  table: Table<TableEntry>;
  columnId: string;
  columnName: string;
  columnKey: FormulaColumnKey;
  quantity: QuantityType | null;
  propertySets: PropertySetType[];
  orderColumns: OrderColumnDeepFragment[];
  availableSubstitutions: Substitution[];
  activeQuantityCell: ActiveQuantityCell;
  handleActiveQuantityCellClick: (
    columnName: string,
    selectedEntry: Row<TableEntry>
  ) => void;
  isDrawingQuantityColumn: boolean;
  resolvedValue: ResolvedQuantity | null;
  isSomePropertyMissing: boolean;
  allowSumOnly?: boolean;
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
    manuallyApplyFilters?: boolean;
  };
  showUnitSymbol?: boolean;
}) {
  const formattedValue = useMemo(() => {
    const unitFormatSettings =
      quantity?.unitFormatOverride ?? projectSettings.unitSettings;
    return resolvedValue
      ? formatQuantity(resolvedValue, unitFormatSettings, showUnitSymbol)
      : null;
  }, [
    quantity?.unitFormatOverride,
    projectSettings.unitSettings,
    resolvedValue,
    showUnitSymbol,
  ]);

  const rows = table.getRowModel().rows;

  const { isHistoryDrawerOpen, diff } = useProjectHistory();
  const orderId = row.original.orderId;
  const entryId = row.original.id;
  // eslint-disable-next-line complexity
  const formattedValues = useMemo(() => {
    if (!isHistoryDrawerOpen || !diff) return null;
    const subKey = getEntryDiffPath(rows, row.id);
    const entryKey = `project.orders.${orderId}.orderEntries.${entryId}${subKey}`;
    const retVal = getQuantityAndCustomDiff({
      row,
      columnId,
      diff,
      entryKey,
      resolvedValue,
      projectSettings,
    });
    return retVal;
  }, [
    isHistoryDrawerOpen,
    diff,
    orderId,
    entryId,
    row.id,
    rows,
    columnId,
    resolvedValue?.units,
    projectSettings.unitSettings,
  ]);

  const { t } = useTranslation();

  const tooltipLabel = useMemo(() => {
    if (quantity) {
      const attribute = quantity.calculation?.attribute;
      let propertyCategory = attribute?.category;
      let propertyName = attribute?.name;

      if (attribute) {
        if (attribute.category === SPARKEL_GEOMETRIC_PROPERTY_SET) {
          propertyCategory = t('quantities');
          propertyName = displayNameFromProperty(attribute.name, t);
        } else if (attribute.category === SPARKEL_LOCATION_PROPERTY_SET) {
          propertyCategory = t('location');
          propertyName = displayNameFromProperty(attribute.name, t);
        }

        return `${propertyCategory} / ${propertyName}`;
      }

      return formattedValue;
    }

    return formattedValue;
  }, [formattedValue, quantity, t]);

  const editingOtherColor = useColorModeValue('gray.200', 'gray.600');
  const editingOtherIconColor = useColorModeValue('gray.50', 'gray.600');
  const dynamicValueColor = useColorModeValue('teal.500', 'teal.200');
  if (isHistoryDrawerOpen && formattedValues?.isNumberDiff) {
    return (
      <Box alignSelf="flex-end" paddingRight={2}>
        <DiffNumber
          newValue={formattedValues.new as number | undefined}
          oldValue={formattedValues.old as number | undefined}
        />
      </Box>
    );
  } else if (isHistoryDrawerOpen && formattedValues?.isNumberDiff === false) {
    return (
      <Box overflow="hidden">
        <DiffText
          compact
          newValue={formattedValues.new as string | undefined}
          oldValue={formattedValues.old as string | undefined}
        />
      </Box>
    );
  }
  if (isEditingRow(row, tableMeta)) {
    return (
      <SparkelFormulaInputGroup
        row={row}
        columnKey={columnKey}
        projectId={projectId}
        propertySets={propertySets}
        orderColumns={orderColumns}
        availableSubstitutions={availableSubstitutions}
        columnName={columnName}
        columnId={columnId}
        allowSumOnly={allowSumOnly}
        projectSettings={projectSettings}
      />
    );
  } else if (!quantity) {
    return null;
  } else if (quantity.type === QuantityEnum.Dynamic) {
    const enableGeometricQuantityHighlight =
      (quantity.calculation?.attribute?.category ===
        SPARKEL_GEOMETRIC_PROPERTY_SET ||
        quantity.calculation?.attribute?.category ===
          SPARKEL_LOCATION_PROPERTY_SET) &&
      resolvedValue?.value;

    const isThisQuantityColumActive =
      activeQuantityCell !== null &&
      activeQuantityCell.rowId === row.id &&
      activeQuantityCell.columnName === columnName;

    const loading = isDrawingQuantityColumn && isThisQuantityColumActive;
    const isHighlighted = !isDrawingQuantityColumn && isThisQuantityColumActive;

    if (isNil(resolvedValue?.value) && !isSomePropertyMissing) {
      return null;
    } else {
      const quantiyValueElement = (
        <Tooltip label={tooltipLabel} openDelay={400}>
          {enableGeometricQuantityHighlight ? (
            <Button
              size="xs"
              aria-label={`${row.original.name}-${columnName}`}
              minWidth={2}
              paddingX={2}
              colorScheme={tableMeta.isEditing ? editingOtherIconColor : 'teal'}
              textColor={
                tableMeta.isEditing
                  ? editingOtherColor
                  : isHistoryDrawerOpen
                  ? 'inherit'
                  : dynamicValueColor
              }
              loadingText={
                quantity.calculation?.operation === QuantityOperation.Sum
                  ? formattedValue
                  : ''
              }
              variant="ghost"
              height="100%"
              isLoading={loading}
              isActive={isHighlighted}
              onClick={(event) => {
                event.stopPropagation();
                handleActiveQuantityCellClick(columnName, row);
              }}
              onDoubleClick={(event) => {
                event.stopPropagation();
              }}
            >
              <Text
                textOverflow="ellipsis"
                overflow="hidden"
                display="inline-block"
                fontSize={'xs'}
              >
                {formattedValue}
              </Text>
            </Button>
          ) : (
            <Text
              as="span"
              fontSize={'xs'}
              color={
                tableMeta.isEditing
                  ? editingOtherColor
                  : isHistoryDrawerOpen
                  ? 'inherit'
                  : dynamicValueColor
              }
              aria-label={`${row.original.name}-${columnName}`}
              textAlign="right"
              noOfLines={1}
              paddingRight={2}
              wordBreak="break-all"
            >
              {formattedValue}
            </Text>
          )}
        </Tooltip>
      );
      return (
        <Flex
          direction="row"
          justify="flex-end"
          alignItems="center"
          aria-label={`${row.original.name}-${columnName}`}
          textAlign="right"
        >
          {isSomePropertyMissing ? (
            <MissingPropertyButton
              projectId={projectId}
              resolvedForDbIds={
                row.original.resolvedElementIdentifiersDbid ?? []
              }
              resolvedForQuantity={quantity}
              isDisabled={tableMeta.isEditing}
            />
          ) : null}
          {quantiyValueElement}
        </Flex>
      );
    }
  } else if (quantity.type === QuantityEnum.Derived) {
    if (!isNil(formattedValue)) {
      // Auto-align based on value type: right for numbers, left for text
      const textAlign = isNumericValue(formattedValue) ? 'right' : 'left';
      return (
        <Tooltip
          label={parseAndStringifyDerivedFormula(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            quantity.derivedFormula!,
            orderColumns
          )}
        >
          <Text
            color={
              tableMeta.isEditing
                ? editingOtherColor
                : isHistoryDrawerOpen
                ? 'inherit'
                : dynamicValueColor
            }
            fontSize={'xs'}
            aria-label={`${row.original.name}-${columnName}`}
            noOfLines={1}
            paddingRight={textAlign === 'right' ? 2 : 0}
            paddingLeft={textAlign === 'left' ? 2 : 0}
            wordBreak="break-all"
            textAlign={textAlign}
          >
            {formattedValue}
          </Text>
        </Tooltip>
      );
    } else {
      return (
        <Box textAlign="end" paddingRight={2}>
          <Tooltip
            label={
              <>
                <Text>Warning:</Text>
                <Text as="code">
                  {parseAndStringifyDerivedFormula(
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    quantity.derivedFormula!,
                    orderColumns
                  )}
                </Text>
                <Text> is invalid or resolves to 0</Text>
              </>
            }
          >
            <SparkelIcon
              icon={faExclamationCircle}
              color={tableMeta.isEditing ? editingOtherIconColor : 'orange.300'}
            />
          </Tooltip>
        </Box>
      );
    }
  } else if (quantity.type === QuantityEnum.DatabaseLookup) {
    // Database lookup - display value with lookup indicator
    return (
      <DatabaseLookupCell
        valueColumnId={quantity.databaseLookup?.valueColumnId ?? null}
        formattedValue={formattedValue}
        ariaLabel={`${row.original.name}-${columnName}`}
        isNotFound={isSomePropertyMissing}
        color={
          tableMeta.isEditing
            ? editingOtherColor
            : isHistoryDrawerOpen
            ? 'inherit'
            : dynamicValueColor
        }
      />
    );
  } else {
    // Auto-align based on value type: right for numbers, left for text
    const textAlign = isNumericValue(formattedValue) ? 'right' : 'left';
    return (
      <Tooltip label={formattedValue} openDelay={400}>
        <Text
          fontSize={'xs'}
          noOfLines={1}
          wordBreak="break-all"
          aria-label={`${row.original.name}-${columnName}`}
          textAlign={textAlign}
          paddingRight={textAlign === 'right' ? 2 : 0}
          paddingLeft={textAlign === 'left' ? 2 : 0}
        >
          {formattedValue}
        </Text>
      </Tooltip>
    );
  }
}
interface GetQtyAndCustomValsProps {
  projectSettings?: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
    manuallyApplyFilters?: boolean;
  };
  row: Row<OrderEntryTableRow> | Row<TableEntry>;
  entryKey: string;
  columnId: string;
  columnName?: string;
  diff: Record<string, any>;
  // this value could also be a string | number
  resolvedValue: QuantityWithUnits | null;
}

// TODO: Test this bad boy at a later point
// eslint-disable-next-line complexity
export function getQuantityAndCustomDiff({
  row,
  columnId,
  columnName,
  diff,
  entryKey,
  resolvedValue,
  projectSettings,
}: GetQtyAndCustomValsProps) {
  // I am assuming that this is either used by resolvedqty or custom columns
  let fieldId = row.original.customFields?.find(
    (field) => field.orderColumnId === columnId
  )?.id;
  if (!fieldId && columnName) {
    fieldId = (
      row.original.customFields as OrderEntryTableRow['customFields']
    )?.find((field) => field.orderColumnName === columnName)?.id;
  }
  const customFieldCollectionKey = `${entryKey}.customFields`;
  const customFieldKey = `${customFieldCollectionKey}.${fieldId}`;
  // Case 1: Handle a new field inserted/deleted;
  const customFieldCollectionDiff =
    diff[customFieldCollectionKey] ?? diff[customFieldKey];
  if (fieldId && customFieldCollectionDiff) {
    const { old: colOld, new: colNew } = customFieldCollectionDiff;
    let rawNew, rawOld;
    let isStatic = false;
    if ((!colOld || colOld === '[]') && colNew) {
      const val =
        colNew?.resolvedValue?.value || colNew?.[fieldId]?.resolvedValue?.value;
      const unitas =
        colNew?.resolvedValue?.value || colNew?.[fieldId]?.resolvedValue?.value;
      if (!val) return;
      isStatic = typeof val === 'string';
      rawNew = { value: val, units: unitas };
      rawOld = { value: isStatic ? undefined : 0, units: unitas };
    } else if (colOld && (!colNew || colNew === '[]')) {
      const val =
        colOld?.resolvedValue?.value || colOld?.[fieldId]?.resolvedValue?.value;
      const unitas =
        colOld?.resolvedValue?.value || colOld?.[fieldId]?.resolvedValue?.value;
      if (!val) return;
      isStatic = typeof val === 'string';
      rawOld = { value: val, units: unitas };
      rawNew = { value: isStatic ? undefined : 0, units: unitas };
    }
    if (rawNew && rawOld) {
      if (isStatic) {
        try {
          const rawParsedNew = rawNew.value
            ? JSON.parse(rawNew.value)
            : undefined;
          const rawParsedOld = rawOld.value
            ? JSON.parse(rawOld.value)
            : undefined;
          let parsedNew: string[] | undefined, parsedOld: string[] | undefined;
          if (Array.isArray(rawParsedNew)) {
            parsedNew = rawParsedNew.filter(Boolean).map((element) => {
              const numRep = Number.parseFloat(element);
              if (!Number.isNaN(numRep)) {
                return String(round(numRep, 2));
              }
              return element;
            });
          }
          if (Array.isArray(rawParsedOld)) {
            parsedOld = rawParsedOld.filter(Boolean).map((element) => {
              const numRep = Number.parseFloat(element);
              if (!Number.isNaN(numRep)) {
                return String(round(numRep, 2));
              }
              return element;
            });
          }
          const dedupedNew = Array.from(new Set(parsedNew ?? []));
          const dedupedOld = Array.from(new Set(parsedOld ?? []));
          // Leave the ternary on so that we pass in undefined instead of '' so we get the correct behavior of created
          // Alternatively, updated difftext to also treat '' as a bottom value (the same way it treats undefined)
          // I leave this comment here to crush any temptation to simplify the ternary below to become dedupedNew?.join(', ')
          return {
            new: dedupedNew.length ? dedupedNew.join(', ') : undefined,
            old: dedupedOld.length ? dedupedOld.join(', ') : undefined,
            isNumberDiff: false,
          };
        } catch (e) {
          return {
            new: rawNew.value,
            old: rawOld.value,
            isNumberDiff: false,
          };
        }
      }
      const newOne = formatQuantity(
        rawNew,
        projectSettings?.unitSettings,
        false
      );
      const old = formatQuantity(rawOld, projectSettings?.unitSettings, false);
      return {
        new: Number.parseFloat(newOne),
        old: Number.parseFloat(old),
        isNumberDiff: true,
      };
    }
  }

  // Case 2: Handle updates to an existing field: handled here
  const propertyKey =
    columnId === 'quantity' ? 'resolvedQuantity' : 'customFields';
  const propertyValue =
    columnId === 'quantity'
      ? `${entryKey}.${propertyKey}.value`
      : `${customFieldKey}.resolvedValue.value`;
  const propUnits =
    columnId === 'quantity'
      ? `${entryKey}.${propertyKey}.units`
      : `${customFieldKey}.resolvedValue.units`;

  const unitsDiff = diff[propUnits];
  const valueDiff = diff[propertyValue];
  if (
    typeof valueDiff?.new === 'string' ||
    typeof valueDiff?.old === 'string'
  ) {
    // Try to parse valueDiff contents into an array, if it throws, we know we are in an ordinary
    // Assumption: We do not expect a string that can be parsed into an object to be passed.
    try {
      const rawParsedNew = valueDiff?.new
        ? JSON.parse(valueDiff.new)
        : undefined;
      const rawParsedOld = valueDiff?.old
        ? JSON.parse(valueDiff.old)
        : undefined;
      let parsedNew: string[] | undefined, parsedOld: string[] | undefined;

      if (Array.isArray(rawParsedNew)) {
        parsedNew = rawParsedNew.filter(Boolean).map((element) => {
          const numRep = Number.parseFloat(element);
          if (!Number.isNaN(numRep)) {
            return String(round(numRep, 2));
          }
          return element;
        });
      }
      if (Array.isArray(rawParsedOld)) {
        parsedOld = rawParsedOld.filter(Boolean).map((element) => {
          const numRep = Number.parseFloat(element);
          if (!Number.isNaN(numRep)) {
            return String(round(numRep, 2));
          }
          return element;
        });
      }
      const dedupedNew = Array.from(new Set(parsedNew ?? []));
      const dedupedOld = Array.from(new Set(parsedOld ?? []));
      return {
        new: dedupedNew?.length ? dedupedNew?.join(', ') : undefined,
        old: dedupedOld?.length ? dedupedOld?.join(', ') : undefined,
        isNumberDiff: false,
      };
    } catch (e) {
      return {
        new: valueDiff?.new,
        old: valueDiff?.old,
        isNumberDiff: false,
      };
    }
  }
  if (unitsDiff) {
    // means that the values are different, read values from the diff
    const rawNew = { value: valueDiff?.new, units: unitsDiff.new };
    const rawOld = { value: valueDiff?.old, units: unitsDiff.old };
    // Check for when linkedElement goes to/from zero
    // Assumption: we always have units when we have values
    if (!rawOld.units && rawOld.value === 0) {
      return {
        new: rawNew.value,
        old: 0,
        isNumberDiff: true,
      };
    } else if (!rawNew.units && rawNew.value === 0) {
      return {
        new: 0,
        old: rawOld.value,
        isNumberDiff: true,
      };
    }
    const newOne = formatQuantity(rawNew, projectSettings?.unitSettings, false);
    const old = formatQuantity(rawOld, projectSettings?.unitSettings, false);
    const lengthUnits = projectSettings?.unitSettings?.length?.units
      ? UnitTypeToUnit(projectSettings?.unitSettings.length.units)
      : Unit.M;
    const areaUnits = projectSettings?.unitSettings?.area?.units
      ? UnitTypeToUnit(projectSettings?.unitSettings.area.units)
      : Unit.M2;
    const volumeUnits = projectSettings?.unitSettings?.volume?.units
      ? UnitTypeToUnit(projectSettings?.unitSettings.volume.units)
      : Unit.M3;
    let newUnits = lengthUnits;
    let oldUnits = lengthUnits;
    if ((unitsDiff.new as string)?.includes('^2')) {
      newUnits = areaUnits;
    } else if (unitsDiff.new?.includes('^3')) {
      newUnits = volumeUnits;
    }
    if ((unitsDiff.old as string)?.includes('^2')) {
      oldUnits = areaUnits;
    } else if (unitsDiff.old?.includes('^3')) {
      oldUnits = volumeUnits;
    }
    return {
      new: `${newOne}${formatUnit(newUnits)}`,
      old: `${old}${formatUnit(oldUnits)}`,
      isNumberDiff: false,
    };
  } else if (!unitsDiff && valueDiff) {
    // It means that the units are the same, pick any state and read values form it
    if (
      !resolvedValue?.units &&
      typeof valueDiff?.new === 'number' &&
      typeof valueDiff?.old === 'number'
    ) {
      return {
        new: valueDiff.new,
        old: valueDiff.old,
        isNumberDiff: true,
      };
    }
    if (!resolvedValue?.units) return;
    const rawNew = { value: valueDiff?.new, units: resolvedValue.units };
    const rawOld = { value: valueDiff?.old, units: resolvedValue.units };
    const newOne = formatQuantity(rawNew, projectSettings?.unitSettings, false);
    const old = formatQuantity(rawOld, projectSettings?.unitSettings, false);
    return {
      new: Number.parseFloat(newOne),
      old: Number.parseFloat(old),
      isNumberDiff: true,
    };
  }
}

/**
 * Check if a value looks like a number (including formatted numbers with spaces/commas)
 */
function isNumericValue(value: string | null): boolean {
  if (!value) return false;
  // Remove common number formatting (spaces, commas as thousand separators)
  const cleaned = value.replace(/[\s,]/g, '').replace(',', '.');
  return !isNaN(parseFloat(cleaned)) && isFinite(Number(cleaned));
}

/**
 * Separate component for database lookup cells to fetch column name for tooltip.
 */
function DatabaseLookupCell({
  valueColumnId,
  formattedValue,
  ariaLabel,
  isNotFound,
  color,
}: {
  valueColumnId: string | null;
  formattedValue: string | null;
  ariaLabel: string;
  isNotFound: boolean;
  color: string;
}): React.ReactElement {
  const { t } = useTranslation('table');
  const notFoundColor = useColorModeValue('gray.500', 'gray.400');

  const [{ data }] = useQuery({
    query: GetCompanyDatabaseColumnDocument,
    variables: { id: valueColumnId ?? '' },
    pause: !valueColumnId,
  });

  const columnName = data?.companyDatabaseColumn?.name;
  const tooltipLabel = columnName ?? t('database-lookup');

  // Determine text alignment based on value type
  const textAlign = isNumericValue(formattedValue) ? 'right' : 'left';

  // Show "No item found" when the lookup ran but didn't find a match
  if (isNotFound && !formattedValue) {
    return (
      <Tooltip label={tooltipLabel} openDelay={400}>
        <Text
          color={notFoundColor}
          fontSize={'xs'}
          fontStyle="italic"
          aria-label={ariaLabel}
          noOfLines={1}
          paddingRight={2}
          paddingLeft={textAlign === 'left' ? 2 : 0}
          wordBreak="break-all"
          textAlign={textAlign}
        >
          {t('no-item-found')}
        </Text>
      </Tooltip>
    );
  }

  return (
    <Tooltip label={tooltipLabel} openDelay={400}>
      <Text
        color={color}
        fontSize={'xs'}
        aria-label={ariaLabel}
        noOfLines={1}
        paddingRight={textAlign === 'right' ? 2 : 0}
        paddingLeft={textAlign === 'left' ? 2 : 0}
        wordBreak="break-all"
        textAlign={textAlign}
      >
        {formattedValue ?? '-'}
      </Text>
    </Tooltip>
  );
}
